import { FC } from "react";
import { styled } from "styled-components";
import { ContentfulNavigationItem, ContentfulNavigationRow } from "../lib/contentfulTypes";
import { colors } from "../lib/sharedStyles";
import DesktopMenuLink from "./DesktopMenuLink";
import DesktopNavigationRow from "./DesktopNavigationRow";
import ChevronDown from "./Icons/ChevronDown";

const Menu = styled.div<{ $brand: string; }>`
  display: none;
  position: absolute;
  top: 100%;
  width: 100%;
  left: 0;
  align-items: center;
  background-color: #fff;

  img {
    max-width: 100%;
  }
  // Dawn breakpoint
  @media (min-width: 1150px) {
    display: flex;
  }

  ${({ $brand }) => $brand == 'jackson' && `
    justify-content: center;
    background-color: #020202;
  `}

  ${({ $brand }) => $brand == 'fender' && `
    border-top: 1px solid #EFF0F0;

    span {
      text-underline-position: under;
    }

    @media (min-width: 1150px) {
      padding-left: calc(5% - 10px);
    }
  `}
`;

const TopLevelWrapper = styled.div<{ $brand: string; }>`
  padding: 0 10px;

  &:hover {
    > a {
      ${({ $brand }) => $brand == 'jackson' && `
        color: #fff;
        border-bottom-color: #fff;
        svg path {
          fill: #fff;
        }
      `}

      > span {
        ${({ $brand }) => $brand == 'fender' && `
          text-decoration: underline;
        `}
      }
    }
    
    > div {
      opacity: 1;
      pointer-events: all;
    }
  }
`;

const Menu2 = styled.div<{ $brand: string; }>`
  position: absolute;
  z-index: 10;
  left: 0;
  // This is based on StyledLink height below
  top: 50px;
  width: 100%;
  opacity: 0;
  pointer-events: none;
  background-color: #fff;
  
  ${({ $brand }) => $brand == 'jackson' && `
    top: 64px;
    padding: 32px 16px;
    background-color: #020202;
  `}

  ${({ $brand }) => $brand == 'fender' && `
    border-top: 1px solid ${colors.fender.grey03};
  `}
`;

const Menu2Items = styled.div<{ $brand: string; }>`
  display: flex;
  width: 100%;
  justify-content: space-between;

  ${({ $brand }) => $brand == 'fender' && `
    @media (min-width: 1150px) {
      max-width: 90%;
      margin: 0 auto;
    }

    &:after {
      content: '';
      position: absolute;
      bottom: -100vh;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.5);
      pointer-events: none;
    }
  `}
`;

const Menu2Row = styled.div`
  width: 100%;
`;

const Column = styled.div<{ $brand: string; }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 16px;

  &:not(:last-of-type) {
    ${({ $brand }) => $brand == 'fender' && `
      border-right: 1px solid ${colors.fender.grey03};
    `}
  }

  ${({ $brand }) => $brand == 'fender' && `
    padding: 32px 0;
    margin: 0 0 0 32px;
  `}

  &:first-of-type {
    @media (min-width: 1150px) {
      ${({ $brand }) => $brand == 'fender' && `
        margin-left: 0;
      `}
    }
  }
`;

const Menu3 = styled.div<{ $brand: string; }>`
  display: flex;
  flex-direction: column;
  margin-top: 10px;

  ${({ $brand }) => $brand == 'jackson' && `
    margin-top: 24px;
  `}
`;

const SupportNav = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
  gap: 24px;
  margin-right: 5%;
`;

const SupportItemWrapper = styled.div<{ $hasSubItems: boolean; }>`
  position: relative;

  &:hover {
    > a {
      > span {
        text-decoration: underline;
      }
    }

    > div {
      opacity: 1;
      pointer-events: all;
    }

    > svg {
      transform: rotate(180deg);
    }
  }

  ${({ $hasSubItems }) => $hasSubItems && `
    padding-right: 10px;
  `}

  > svg {
    position: absolute;
    bottom: 22px;
    right: -4px;
    transition: transform 300ms ease;
  }
`;

const SupportDropdown = styled.div`
  position: absolute;
  z-index: 10;
  top: 50px;
  right: 0;
  width: 300px;
  opacity: 0;
  pointer-events: none;
  border-radius: 0px 0px 2px 2px;
  border: 1px solid #E5E5E5;
  background-color: #fff;
  // 10px margin-bottom on links
  padding: 16px 16px 6px 16px;
`;

interface DesktopMenuProps {
  brand: string;
  items: Array<ContentfulNavigationItem>;
  supportNav: Array<ContentfulNavigationItem>;
}

const DesktopMenu: FC<DesktopMenuProps> = ({ brand, items, supportNav = [] }) => (
  <Menu $brand={brand}>
    {items.map(item1 => (
      <TopLevelWrapper key={item1.sys.id} className="level-1-wrapper" $brand={brand}>
        <DesktopMenuLink brand={brand} item={item1} level={1} />
        {(item1.fields.subItems) && (
          <Menu2 className="level-2-wrapper" $brand={brand}>
            <Menu2Items $brand={brand}>
              {item1.fields.subItems.map(item2 => {
                if (item2.sys.contentType.sys.id == 'navigationItem') {
                  return (
                    <Column $brand={brand} key={item2.sys.id}>
                      <DesktopMenuLink brand={brand} item={item2} level={2} />
                      <Menu3 $brand={brand}>
                        {item2.fields.subItems?.map(item3 => (
                          <DesktopMenuLink key={item3.sys.id} brand={brand} item={item3} level={3} />
                        ))}
                      </Menu3>
                    </Column>
                  );
                }
              })}
            </Menu2Items>
            {/* Render Navigation Rows below other menu items */}
            <Menu2Row>
              {item1.fields.subItems.map(item2 => {
                if (item2.sys.contentType.sys.id == 'navigationRow') {
                  const rowItem = item2 as ContentfulNavigationRow;
                  return (
                    <DesktopNavigationRow 
                      {...rowItem} 
                      brand={brand} 
                      key={rowItem.sys.id}
                    />
                  )
                }
              })}
            </Menu2Row>
          </Menu2>
        )}
      </TopLevelWrapper>
    ))}
    {(supportNav.length > 0) && (
      <SupportNav>
        {supportNav.map(supportItem => {
          const hasSubItems = supportItem.fields.subItems && supportItem.fields.subItems.length > 0 || false;

          return (
            <SupportItemWrapper key={supportItem.sys.id} $hasSubItems={hasSubItems}>
              <DesktopMenuLink brand={brand} item={supportItem} level={1} />
              {(supportItem.fields.subItems) && (
                <>
                  <ChevronDown />
                  <SupportDropdown>
                    {supportItem.fields.subItems.map(supportSubItem => (
                      <DesktopMenuLink 
                        brand={brand} 
                        item={supportSubItem} 
                        key={supportSubItem.sys.id}
                        level={3} 
                        inSupportNavDropdown
                      />
                    ))}
                  </SupportDropdown>
                </>
              )}
            </SupportItemWrapper>
          );
        })}
      </SupportNav>
    )}
  </Menu>
);

export default DesktopMenu;